import { useEffect, useState } from "react";
import {
  Box, IconButton, LinearProgress, Modal, Sheet, Stack, Typography, CircularProgress,
} from "@mui/joy";
import { motion } from "framer-motion";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { APIGetProfileCompatibility } from "../../api_quiz";

/** Модалка «Почему подходит»: общий % + разбивка по параметрам.
 * props: open, profile ({id, name, ...}), token, onClose */
export default function CompatibilityModal({ open, profile, token, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !profile?.id) return;
    let alive = true;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const res = await APIGetProfileCompatibility(token, profile.id);
        if (alive) setData(res);
      } catch (e) {
        /* ignore */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, profile?.id, token]);

  const percent = data?.compatibility;
  const breakdown = data?.breakdown || [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: "blur(6px)", backgroundColor: "rgba(20,10,40,0.4)" },
        },
      }}
    >
      <Sheet
        variant="outlined"
        component={motion.div}
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 24 }}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: 440 },
          maxHeight: "85vh",
          overflowY: "auto",
          borderRadius: "xl",
          p: 3,
          boxShadow: "lg",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography level="title-lg">Совместимость</Typography>
          <IconButton size="sm" variant="plain" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {profile?.ai_reason ? (
          <Sheet
            variant="soft"
            color="primary"
            sx={{ borderRadius: "lg", p: 1.5, mb: 1.5, display: "flex", gap: 1, alignItems: "flex-start" }}
          >
            <AutoAwesomeRoundedIcon color="primary" fontSize="small" />
            <Typography level="body-sm">{profile.ai_reason}</Typography>
          </Sheet>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : percent == null ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ color: "text.tertiary" }}>
              Пока недостаточно данных — пройдите больше тестов, чтобы увидеть совместимость
              {profile?.name ? ` с ${profile.name}` : ""}.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2 }}>
              <Box
                component={motion.div}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                sx={{
                  width: 110,
                  height: 110,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, var(--joy-palette-primary-400), var(--joy-palette-primary-600))",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  boxShadow: "0 12px 30px rgba(124,58,237,0.4)",
                }}
              >
                <FavoriteRoundedIcon />
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 28, lineHeight: 1 }}>
                  {percent}%
                </Typography>
              </Box>
              {profile?.name && (
                <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 1.5, textAlign: "center" }}>
                  с {profile.name}
                </Typography>
              )}
            </Box>

            <Typography level="title-sm" sx={{ mt: 1, mb: 1 }}>По параметрам</Typography>
            <Stack spacing={1.5}>
              {breakdown.length === 0 && (
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  Нет общих параметров для сравнения.
                </Typography>
              )}
              {breakdown.map((b, i) => (
                <Box
                  key={b.trait_code}
                  component={motion.div}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                    <Typography level="body-sm">{b.trait_name}</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 700, color: "primary.600" }}>
                      {b.match}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    determinate
                    value={b.match}
                    thickness={7}
                    color={b.match >= 66 ? "success" : b.match >= 33 ? "primary" : "warning"}
                    sx={{ borderRadius: 10 }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Sheet>
    </Modal>
  );
}